import { useState, useEffect, useCallback } from 'react';
import { AgentAvatar, parseSeed } from '../AgentAvatar';
import { useWallet } from '../../hooks/useWallet';
import { useModalAlert } from '../ModalAlert';
import { BUCKET_LABELS } from '../research/bucketLabels';

/**
 * Right-side slide-out drawer for quick agent configuration.
 * Per wireframe wf-v2.jsx WorkspaceV2 drawer (360px).
 *
 * Shows agent identity + dropdowns for research strategy, model,
 * recommendation prompt. Deep config still lives in the Setup tab.
 */
export function PersonaDrawer({ agent, portfolio, config, onSave, onClose }) {
  const { jwtToken } = useWallet();
  const { showError, showSuccess } = useModalAlert();
  const apiBase = config?.apiBaseUrl || 'http://localhost:8000';

  const [researchConfigs, setResearchConfigs] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedBucket, setSelectedBucket] = useState('');
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [selectedModel, setSelectedModel] = useState(agent?.model_name || '');
  const [maxHistory, setMaxHistory] = useState(agent?.max_history_messages ?? 10);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!agent) return;
    setSelectedModel(agent.model_name || '');
    setMaxHistory(agent.max_history_messages ?? 10);
  }, [agent]);

  // Fetch dropdown data on mount
  useEffect(() => {
    if (!jwtToken || !agent) return;

    const headers = { Authorization: `Bearer ${jwtToken}` };

    // Research configs
    fetch(`${apiBase}/agents/${agent.id}/research/config`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const configs = Array.isArray(data) ? data : (data.configs || []);
        setResearchConfigs(configs);
        const active = configs.find(c => c.is_active);
        if (active) setSelectedBucket(active.bucket);
      })
      .catch(() => setResearchConfigs([]));

    // Prompts
    fetch(`${apiBase}/agents/${agent.id}/prompts`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setPrompts(data);
        const active = data.find(p => p.is_active);
        if (active) setSelectedPromptId(String(active.id));
      })
      .catch(() => setPrompts([]));

    // Models
    fetch(`${apiBase}/models`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const modelList = Array.isArray(data) ? data : (data.models || []);
        setModels(modelList);
      })
      .catch(() => setModels([]));
  }, [agent, jwtToken, apiBase]);

  const handleSave = useCallback(async () => {
    if (!agent || saving) return;
    setSaving(true);
    const headers = { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' };

    const checkResponse = async (res) => {
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}: ${res.statusText}`);
      }
      return res;
    };

    try {
      // Save agent identity (model cascades to all configs/prompts)
      await checkResponse(await fetch(`${apiBase}/agents/${agent.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          max_history_messages: maxHistory,
          ...(selectedModel ? { model_name: selectedModel } : {}),
        }),
      }));

      // Activate or create research config for selected bucket
      if (selectedBucket) {
        const existing = researchConfigs.find(c => c.bucket === selectedBucket);
        if (existing) {
          await checkResponse(await fetch(`${apiBase}/agents/${agent.id}/research/config/${existing.id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ is_active: true }),
          }));
        } else {
          await checkResponse(await fetch(`${apiBase}/agents/${agent.id}/research/config`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              bucket: selectedBucket,
              methodology_prompt: `${selectedBucket} research strategy`,
              is_active: true,
            }),
          }));
        }
      }

      // Activate selected recommendation prompt
      if (selectedPromptId) {
        const currentActive = prompts.find(p => p.is_active);
        if (!currentActive || String(currentActive.id) !== selectedPromptId) {
          await checkResponse(await fetch(`${apiBase}/agents/${agent.id}/prompts/${selectedPromptId}/activate`, {
            method: 'PUT',
            headers,
          }));
        }
      }

      showSuccess('Saved', 'Agent configuration updated.', { autoClose: true });
      if (onSave) await onSave();
      onClose();
    } catch (err) {
      showError('Save Failed', err.message);
    } finally {
      setSaving(false);
    }
  }, [agent, maxHistory, selectedModel, selectedBucket, selectedPromptId, researchConfigs, prompts, jwtToken, apiBase, saving, onSave, onClose, showSuccess, showError]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!agent) return null;

  const { avatarSeed } = parseSeed(agent.avatar_seed);
  const selectModelOptions = selectedModel && !models.some((m) => (typeof m === 'string' ? m : m.id) === selectedModel)
    ? [selectedModel, ...models]
    : models;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      {/* Drawer */}
      <div className="fixed top-0 right-0 bottom-0 w-[360px] max-w-[90vw] bg-slate-900 border-l border-slate-700 shadow-xl z-50 flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-lg font-bold text-white">Agent configuration</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-sm">
            ✕ <span className="text-xs text-slate-500">esc</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-4 p-4">
          {/* Agent identity row */}
          <div className="flex items-center gap-3">
            <AgentAvatar name={agent.name} seed={avatarSeed} size={48} />
            <div className="min-w-0">
              <div className="font-bold text-white truncate">{agent.name}</div>
              <div className="text-xs text-slate-400">
                primary on {portfolio?.portfolio_name}
              </div>
            </div>
          </div>

          <hr className="border-slate-700 border-dashed" />

          {/* Research strategy */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wide block mb-1">Research strategy</label>
            <select
              value={selectedBucket}
              onChange={(e) => setSelectedBucket(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500"
            >
              {Object.entries(BUCKET_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}{researchConfigs.some(c => c.bucket === key && c.is_active) ? ' (active)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Model */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wide block mb-1">Model</label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500"
            >
              {selectModelOptions.map(m => {
                const id = typeof m === 'string' ? m : m.id;
                const label = typeof m === 'string' ? m : (m.display_name || m.id);
                return <option key={id} value={id}>{label}</option>;
              })}
            </select>
          </div>

          {/* Recommendation prompt */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wide block mb-1">Recommendation prompt</label>
            <select
              value={selectedPromptId}
              onChange={(e) => setSelectedPromptId(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500"
            >
              {prompts.map(p => (
                <option key={p.id} value={String(p.id)}>
                  {p.name || p.version_tag || `Prompt #${p.id}`}{p.is_active ? ' (active)' : ''}
                </option>
              ))}
            </select>
            <a
              href={`/workspace/${portfolio?.id}?tab=setup`}
              className="text-xs text-cyan-400 hover:text-cyan-300 mt-1 inline-block"
            >
              → full editor
            </a>
          </div>

          {/* Max history */}
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wide block mb-1">Max history messages</label>
            <input
              type="number"
              min={1}
              max={100}
              value={maxHistory}
              onChange={(e) => setMaxHistory(parseInt(e.target.value, 10) || 10)}
              className="w-20 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500"
            />
          </div>

          <hr className="border-slate-700 border-dashed" />

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex-1 py-2 px-4 rounded text-white text-sm font-medium transition-colors ${
                saving ? 'bg-cyan-900 opacity-50' : 'bg-cyan-600 hover:bg-cyan-500'
              }`}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={onClose}
              className="py-2 px-4 rounded bg-slate-700 text-slate-300 text-sm hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
